import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isWithinInterval, isBefore, isAfter, addMonths, subMonths } from 'date-fns';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import { supabase } from '../../lib/supabase';
import EventChiclet from './EventChiclet';
import { Calendar, AlertCircle, ChevronLeft, ChevronRight, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import { extractCountryFromEvent } from '../../utils/countryExtractor';

const MAX_VISIBLE_EVENTS = 5;

interface CalendarGridProps {
  googleCalendarUrl: string;
  isSubscribed: boolean;
  calendarId: string;
  isPublic?: boolean;
  paymentType?: 'free' | 'subscription' | 'one_time';
  addressVisibility?: 'public' | 'subscribers' | 'private';
  selectedCountries?: string[];
  selectedSources?: string[];
}

export default function CalendarGrid({
  googleCalendarUrl,
  isSubscribed,
  calendarId,
  isPublic = true,
  paymentType = 'free',
  addressVisibility = 'subscribers',
  selectedCountries = [],
  selectedSources = []
}: CalendarGridProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { events: icsEvents, loading: icsLoading, error } = useGoogleCalendar(googleCalendarUrl);
  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const today = new Date();

  useEffect(() => {
    if (!icsLoading && icsEvents.length === 0 && calendarId) {
      fetchLocalEvents();
    }
  }, [icsLoading, icsEvents.length, calendarId]);

  const fetchLocalEvents = async () => {
    setLocalLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('calendar_events')
        .select('id, title, start_datetime, end_datetime, description, location, is_recurring, recurrence_pattern, source')
        .eq('calendar_id', calendarId)
        .in('sync_status', ['synced', 'pending_google_sync'])
        .order('start_datetime', { ascending: true });

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        const mapped = data.map(e => ({
          id: e.id,
          title: e.title,
          start: new Date(e.start_datetime),
          end: new Date(e.end_datetime),
          description: e.description || undefined,
          location: e.location || undefined,
          isRecurring: e.is_recurring || false,
          recurrenceRule: e.recurrence_pattern || undefined,
          allDay: false,
          source: e.source || undefined,
        }));
        setLocalEvents(mapped);
      }
    } catch (err) {
      console.error('Error fetching local events:', err);
    } finally {
      setLocalLoading(false);
    }
  };

  const events = icsEvents.length > 0 ? icsEvents : localEvents;
  const loading = icsLoading || localLoading;
  const start = startOfMonth(currentMonth);
  const end = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start, end });

  // Define weekdays starting with Sunday
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const baseVisibleEvents = isPublic ? events : isSubscribed ? events : [];
  const countryFiltered = selectedCountries.length > 0
    ? baseVisibleEvents.filter(event => {
        const country = extractCountryFromEvent(event);
        return country && selectedCountries.includes(country.code);
      })
    : baseVisibleEvents;
  const visibleEvents = selectedSources.length > 0
    ? countryFiltered.filter(event =>
        event.source && selectedSources.includes(event.source.toLowerCase().trim())
      )
    : countryFiltered;
  const isPaid = paymentType === 'subscription' || paymentType === 'one_time';

  const toggleDayExpanded = (dayKey: string) => {
    setExpandedDays(prev => ({ ...prev, [dayKey]: !prev[dayKey] }));
  };

  // Determine if address should be shown
  const showAddress = addressVisibility === 'public' || 
    (addressVisibility === 'subscribers' && isSubscribed) ||
    addressVisibility === 'private';

  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="p-4 border-b border-purple-100">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold text-gray-900 flex-1">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center rounded-md border border-gray-200">
            <button
              onClick={handlePreviousMonth}
              className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-l-md border-r border-gray-200 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-r-md transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="p-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
          {error.includes('CORS proxy') && (
            <a 
              href="https://cors-anywhere.herokuapp.com/corsdemo"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center text-sm text-red-600 hover:text-red-700 underline"
            >
              Click here to enable the CORS proxy
            </a>
          )}
        </div>
      )}

      {!isPublic && !isSubscribed && (
        <div className="p-4 bg-amber-50 border-b border-amber-100">
          <div className="flex items-center space-x-2 text-amber-800">
            <Lock className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">Sign in and subscribe to view events for this private calendar</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-7">
        {weekdays.map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-gray-500 border-b border-purple-100"
          >
            {day}
          </div>
        ))}
        {days.map((day) => {
          // Get events that occur on this day (including multi-day events)
          const dayEvents = visibleEvents.filter(event => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            
            // For all-day events, check if the day matches the start date
            if (event.allDay) {
              return isSameDay(day, eventStart);
            }
            
            // For timed events, check if this day falls within the event's duration
            return isWithinInterval(day, { start: eventStart, end: eventEnd }) ||
                   isSameDay(eventStart, day) ||
                   isSameDay(eventEnd, day);
          });

          // Calculate the day of week (0-6, where 0 is Sunday)
          const dayOfWeek = day.getDay();

          const dayKey = format(day, 'yyyy-MM-dd');
          const isExpanded = expandedDays[dayKey];
          const hasOverflow = dayEvents.length > MAX_VISIBLE_EVENTS;
          const displayEvents = hasOverflow && !isExpanded
            ? dayEvents.slice(0, MAX_VISIBLE_EVENTS)
            : dayEvents;
          const hiddenCount = dayEvents.length - MAX_VISIBLE_EVENTS;

          return (
            <div
              key={day.toISOString()}
              style={{ gridColumnStart: dayOfWeek + 1 }}
              className={`min-h-[100px] p-2 border-b border-r border-purple-100 transition-colors ${
                dayEvents.length > 0 ? 'hover:bg-purple-50' : ''
              } ${isSameDay(day, today) ? 'bg-purple-50/30' : ''}`}
            >
              <time
                dateTime={dayKey}
                className={`block text-sm font-medium mb-1 ${
                  isSameDay(day, today)
                    ? 'text-purple-600'
                    : 'text-gray-900'
                }`}
              >
                {format(day, 'd')}
              </time>
              <div className="space-y-1">
                {displayEvents.map(event => {
                  const eventStart = new Date(event.start);
                  const eventEnd = new Date(event.end);
                  const isFirstDay = isSameDay(day, eventStart);
                  const isLastDay = isSameDay(day, eventEnd);
                  const isMiddleDay = isAfter(day, eventStart) && isBefore(day, eventEnd);

                  return (
                    <EventChiclet
                      key={event.id}
                      event={event}
                      showLocation={showAddress}
                      calendarId={calendarId}
                      isFirstDay={isFirstDay}
                      isLastDay={isLastDay}
                      isMiddleDay={isMiddleDay}
                      isPaid={isPaid}
                      isSubscribed={isSubscribed}
                    />
                  );
                })}
                {hasOverflow && (
                  <button
                    onClick={() => toggleDayExpanded(dayKey)}
                    className="flex items-center gap-0.5 text-xs text-gray-500 hover:text-gray-700 font-medium py-0.5 transition-colors w-full"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3" />
                        <span>Show less</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" />
                        <span>+{hiddenCount} more</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {loading ? (
        <div className="p-4 text-center text-gray-500 border-t border-purple-100">
          Loading events...
        </div>
      ) : visibleEvents.length === 0 && !error && (
        <div className="p-8 text-center text-gray-500">
          <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm">
            {!isPublic && !isSubscribed 
              ? 'Subscribe to view events'
              : 'No events found for this month.'}
          </p>
          {!googleCalendarUrl && (
            <p className="mt-1 text-sm text-gray-400">
              Calendar URL needs to be configured.
            </p>
          )}
        </div>
      )}
    </div>
  );
}